import {testIdProps} from '@github-ui/test-id-props'
import {AlertIcon, CheckCircleIcon, InfoIcon, StopIcon, XIcon} from '@primer/octicons-react'
import {Octicon} from '@primer/react/deprecated'
import {clsx} from 'clsx'
import {type ComponentProps, forwardRef, useCallback, useEffect, useRef} from 'react'

import styles from './toast.module.css'
import {TOAST_ANIMATION_LENGTH, type ToastItem, ToastType} from './types'

type IToastCloseButtonProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'>

const StyledButton = forwardRef<HTMLButtonElement, ComponentProps<'button'>>(({className, ...props}, ref) => (
  <button {...props} className={clsx(styles.styledButton, className)} ref={ref} />
))
StyledButton.displayName = 'StyledButton'

const ToastCloseButton = (props: IToastCloseButtonProps) => (
  <StyledButton aria-label="Close" {...props}>
    <Octicon icon={XIcon} />
  </StyledButton>
)

const DefaultIcon = <Octicon icon={InfoIcon} className={styles.Octicon} />
const SuccessIcon = <Octicon icon={CheckCircleIcon} className={styles.Octicon} />
export const WarningIcon = <Octicon icon={AlertIcon} className={styles.Octicon} />
const ErrorIcon = <Octicon icon={StopIcon} className={styles.Octicon} />

const stateMap = {
  [ToastType.default]: DefaultIcon,
  [ToastType.success]: SuccessIcon,
  [ToastType.warning]: WarningIcon,
  [ToastType.error]: ErrorIcon,
}

export const IconContainer = forwardRef<HTMLDivElement, ComponentProps<'div'>>(({className, ...props}, ref) => (
  <div {...props} className={clsx(styles.iconContainer, className)} ref={ref} />
))
IconContainer.displayName = 'IconContainer'

export const StyledToast = forwardRef<HTMLDivElement, ComponentProps<'div'>>(({className, style, ...props}, ref) => (
  <div
    ref={ref}
    {...props}
    style={{'--toast-animation-length': `${TOAST_ANIMATION_LENGTH}ms`, ...style}}
    className={clsx(styles.styledToast, className)}
  />
))
StyledToast.displayName = 'StyledToast'

export const ToastAction = forwardRef<HTMLButtonElement, ComponentProps<'button'>>(({className, ...props}, ref) => (
  <button {...props} className={clsx(styles.toastAction, className)} ref={ref} />
))
ToastAction.displayName = 'ToastAction'

interface ToastProps {
  removeToast?: (id: number) => void
  startRemovingToast?: (id: number) => void
  toast: ToastItem
}

/**
 * Component for rendering a toast within the application
 */
const Toast = (props: ToastProps) => {
  const {toast, startRemovingToast} = props
  const callToActionRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (callToActionRef.current && event.ctrlKey && event.key === 't') {
        callToActionRef.current.focus()
        toast.timeout?.cancel()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [toast])

  const toastIcon = toast.icon ? <Octicon icon={toast.icon} className={styles.Octicon_1} /> : stateMap[toast.type]

  const handleActionClick = useCallback(() => {
    toast.action?.handleClick()
    if (startRemovingToast) {
      startRemovingToast(toast.id)
    }
  }, [toast, startRemovingToast])

  return (
    <StyledToast role="status" className={toast.className} {...testIdProps('toast')}>
      <IconContainer className={clsx(styles.IconContainer, styles[toast.type])}>{toastIcon}</IconContainer>
      <div className={styles.Box}>
        <span className={styles.Text}>{toast.message}</span>
        {toast.action && (
          <ToastAction
            ref={callToActionRef}
            onClick={handleActionClick}
            {...testIdProps('toast-action')}
            className={styles.ToastAction}
          >
            {toast.action.text}
          </ToastAction>
        )}
      </div>
      {startRemovingToast && <ToastCloseButton onClick={() => startRemovingToast(toast.id)} />}
    </StyledToast>
  )
}

export default Toast

try{ ToastCloseButton.displayName ||= 'ToastCloseButton' } catch {}